import * as React from "react";

import {AudioCache} from "@khan/guide-core-helpers";
import type {Voice} from "@khan/guide-types";

export interface GuideAudioController {
    audioCache: Readonly<AudioCache>;
    audioKey: string;
    isPlaying: boolean;
    isTextToSpeechSupported: boolean;
    isVoiceOn: boolean;
    pause: () => void;
    play: (audioKey: string) => void;
    selectedVoice: Voice | null;
    setAudioCache: React.Dispatch<React.SetStateAction<AudioCache>>;
    setIsVoiceOn: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedVoice: React.Dispatch<React.SetStateAction<Voice | null>>;
    stop: () => void;
    chunkIndex: number;
    outputVolumeAvg: number;
}

/**
 * The default state of the guide audio context.
 */
const defaultState: GuideAudioController = Object.freeze({
    audioCache: new AudioCache(),
    audioKey: "",
    isPlaying: false,
    isTextToSpeechSupported: false,
    isVoiceOn: false,
    pause: () => {},
    play: () => {},
    selectedVoice: null,
    setAudioCache: () => {},
    setIsVoiceOn: () => {},
    setSelectedVoice: () => {},
    stop: () => {},
    chunkIndex: 0,
    outputVolumeAvg: 0,
});

/**
 * A React context that provides access to the guide audio state.
 *
 * Please do not use this directly. Instead use the GuideAudioProvider and
 * the useGuideAudioController hook.
 */
export const GuideAudioContext: React.Context<GuideAudioController> =
    React.createContext(defaultState);
GuideAudioContext.displayName = "GuideAudioContext";
